import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

interface User {
  id: string;
  name: string;
  role: string;
  stationId?: string;
}

interface Station {
  id: string;
  name: string;
  location: string;
  is_active?: boolean;
  /** Per-station kiosk UI scale (%) — default 100. Applied as zoom on the kiosk root. */
  ui_scale?: number;
  /** Per-station kiosk text size (%) — default 100. Applied as font-size on the kiosk root. */
  text_scale?: number;
}

interface RadioStation {
  id: string;
  name: string;
  city: string;
  logo: string;
  streamUrl: string;
}

interface PackingStationContextType {
  user: User | null;
  station: Station | null;
  isLoading: boolean;
  login: (pin: string, stationId: string) => Promise<void>;
  logout: () => void;
  updateStation: (station: Station) => void;
  clearStation: () => void;
  apiUrl: string;
  // Radio state
  radioRef: React.RefObject<HTMLAudioElement>;
  radioStation: RadioStation | null;
  radioPlaying: boolean;
  radioVolume: number;
  radioMuted: boolean;
  setRadioStation: (station: RadioStation | null) => void;
  setRadioPlaying: (playing: boolean) => void;
  setRadioVolume: (volume: number) => void;
  setRadioMuted: (muted: boolean) => void;
}

const PackingStationContext = createContext<PackingStationContextType | undefined>(undefined);

export const usePackingStation = () => {
  const context = useContext(PackingStationContext);
  if (!context) {
    throw new Error('usePackingStation must be used within PackingStationProvider');
  }
  return context;
};

interface PackingStationProviderProps {
  children: ReactNode;
}

export const PackingStationProvider: React.FC<PackingStationProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [station, setStation] = useState<Station | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const apiUrl = API_URL;
  
  // Radio state
  const radioRef = useRef<HTMLAudioElement>(null);
  const [radioStation, setRadioStation] = useState<RadioStation | null>(null);
  const [radioPlaying, setRadioPlaying] = useState(false);
  const [radioVolume, setRadioVolume] = useState(0.7);
  const [radioMuted, setRadioMuted] = useState(false);

  // Załaduj stan radia z localStorage
  useEffect(() => {
    const savedRadioState = localStorage.getItem('radio-state');
    if (savedRadioState) {
      try {
        const state = JSON.parse(savedRadioState);
        setRadioVolume(state.volume || 0.7);
        setRadioMuted(state.muted || false);
      } catch (e) {
        console.error('Error loading radio state:', e);
      }
    }
  }, []);

  // Ustaw głośność audio
  useEffect(() => {
    if (radioRef.current) {
      radioRef.current.volume = radioVolume;
      radioRef.current.muted = radioMuted;
    }
  }, [radioVolume, radioMuted]);

  useEffect(() => {
    // Sprawdź czy użytkownik jest zalogowany
    const checkAuth = async () => {
      const authToken = localStorage.getItem('packingStationAuth');
      const token = localStorage.getItem('packingStationToken');
      const savedUser = localStorage.getItem('packingStationUser');
      const savedStation = localStorage.getItem('packingStationStation');

      if (authToken === 'true' && token && savedUser && savedStation) {
        // Ustaw token dla axios
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        try {
          // Zweryfikuj token
          const response = await axios.get(`${apiUrl}/api/auth/verify`);
          if (response.data.valid) {
            setUser(JSON.parse(savedUser));
            setStation(JSON.parse(savedStation));
          } else {
            // Token nieprawidłowy, wyczyść dane
            logout();
          }
        } catch (error) {
          // Token wygasł lub jest nieprawidłowy
          logout();
        }
      }
      setIsLoading(false);
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  const login = async (pin: string, stationId: string) => {
    try {
      const response = await axios.post(`${apiUrl}/api/auth/packing-station/login`, {
        pin,
        stationId,
      });

      if (response.data.success) {
        const { user: userData, station: stationData, token } = response.data;

        setUser(userData);
        setStation(stationData);

        // Zapisz dane w localStorage (jeden magazyn tokenu operatora — v2)
        localStorage.setItem('packingStationAuth', 'true');
        localStorage.setItem('packingStationToken', token);
        localStorage.setItem('packflow.operator_token', token);
        localStorage.setItem('packingStationUser', JSON.stringify(userData));
        localStorage.setItem('packingStationStation', JSON.stringify(stationData));
        
        // Ustaw token dla wszystkich przyszłych requestów
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.error || 'Błąd logowania');
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${apiUrl}/api/auth/packing-station/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Zatrzymaj radio przy wylogowaniu
    if (radioRef.current) {
      radioRef.current.pause();
      radioRef.current.src = '';
    }
    setRadioPlaying(false);
    setRadioStation(null);
    
    setUser(null);
    setStation(null);
    localStorage.removeItem('packingStationAuth');
    localStorage.removeItem('packingStationToken');
    localStorage.removeItem('packflow.operator_token');
    localStorage.removeItem('packingStationUser');
    localStorage.removeItem('packingStationStation');
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateStation = (newStation: Station) => {
    setStation(newStation);
    localStorage.setItem('packingStationStation', JSON.stringify(newStation));
  };

  const clearStation = () => {
    setStation(null);
    localStorage.removeItem('packingStationStation');
  };

  return (
    <PackingStationContext.Provider
      value={{
        user,
        station,
        isLoading,
        login,
        logout,
        updateStation,
        clearStation,
        apiUrl,
        // Radio state
        radioRef,
        radioStation,
        radioPlaying,
        radioVolume,
        radioMuted,
        setRadioStation,
        setRadioPlaying,
        setRadioVolume,
        setRadioMuted,
      }}
    >
      {children}
      {/* Global audio element */}
      <audio
        ref={radioRef}
        src={radioStation?.streamUrl}
        onPlay={() => setRadioPlaying(true)}
        onPause={() => setRadioPlaying(false)}
      />
    </PackingStationContext.Provider>
  );
};
